import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/utils2";
import { waAnnouncement } from "@/lib/whatsapp";
import { useAutoTranslate, TranslationBadge } from "@/components/AutoTranslate";

export default function Announcements() {
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const [anns, setAnns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", pinned: false });

  const load = () => api.get("/announcements").then((r) => setAnns(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error(t("title"));
    await api.post("/announcements", { ...form, images: [] });
    toast.success(t("saved")); setOpen(false); setForm({ title: "", body: "", pinned: false }); load();
  };

  return (
    <div className="space-y-4" data-testid="announcements-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("announcements")}</h1>
        {isRole("president") && (
          <button onClick={() => setOpen(!open)} data-testid="new-announcement-btn"
            className="h-10 px-4 rounded-full bg-brand text-white font-semibold text-sm flex items-center gap-1">
            <Icons.Plus className="h-4 w-4" /> {t("new_announcement")}
          </button>
        )}
      </div>

      {open && (
        <div className="rounded-2xl bg-card border border-border p-4 space-y-3" data-testid="announcement-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("title")}
            data-testid="ann-title" className="w-full h-12 rounded-xl border border-input bg-background px-3 outline-none" />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder={t("message")} rows={3}
            data-testid="ann-body" className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none" />
          <label className="flex items-center gap-2 font-semibold">
            <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} data-testid="ann-pin" />
            {t("pin")}
          </label>
          <button onClick={create} data-testid="ann-submit" className="w-full h-12 rounded-xl bg-brand text-white font-bold">{t("create")}</button>
        </div>
      )}

      {anns.length === 0 && <p className="py-16 text-center text-muted-foreground">{t("no_announcements")}</p>}
      <div className="space-y-3">
        {anns.map((a) => (
          <AnnouncementCard key={a.id} a={a} t={t} lang={lang} />
        ))}
      </div>
    </div>
  );
}

function AnnouncementCard({ a, t, lang }) {
  const tr = useAutoTranslate({ title: a.title, body: a.body }, a.lang);
  const share = () => window.open(waAnnouncement({ title: tr.values.title, body: tr.values.body, lang }), "_blank");
  return (
    <div className="rounded-2xl bg-card border border-border p-4" data-testid={`announcement-${a.id}`}>
      <div className="flex items-start gap-2">
        {a.pinned && <Icons.Pin className="h-5 w-5 text-terracotta shrink-0 mt-0.5" />}
        <div className="flex-1">
          <p className="font-bold text-lg">{tr.values.title}</p>
          <p className="text-muted-foreground mt-1">{tr.values.body}</p>
          <TranslationBadge tr={tr} />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">{a.author} · {fmtDate(a.created_at, lang)}</p>
            <button onClick={share} data-testid={`share-announcement-${a.id}`}
              className="h-8 px-3 rounded-lg bg-[#25D366] text-white font-semibold text-xs flex items-center gap-1.5">
              <Icons.Send className="h-3.5 w-3.5" /> {t("share_whatsapp")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
